import React from "react";
import { useOrganisation, User, useUser } from "~/auth-layer";
import {
  camelCaseKeys,
  defaultReactOptions,
  LDProvider,
  useLDClient,
} from "launchdarkly-react-client-sdk";
import { env } from "~/env";
import { useIframeDetection } from "./IframeDetection";
import { readHeadlessFlagOverrides } from "./headlessFlagOverrides";

/**
 * The context `useFlags()` reads from. Supplying it directly is how a headless
 * run gets flag values without a LaunchDarkly client (and so without any
 * network call) — see {@link readHeadlessFlagOverrides}.
 */
const LDFlagsContext = defaultReactOptions.reactContext;

export const LaunchDarklyProvider = ({
  children,
}: {
  children: React.ReactChild;
}) => {
  const { isInIframe } = useIframeDetection();
  if (isInIframe) {
    return <>{children}</>;
  }

  // Headless runs never identify against LaunchDarkly, so the client would sit
  // uninitialised and every flag would read falsy. Serve the flag context
  // ourselves instead, from whatever the test asked for — no client, no network.
  if (env.HEADLESS_TESTING) {
    return (
      <LDFlagsContext.Provider
        value={{
          flags: camelCaseKeys(readHeadlessFlagOverrides()),
          flagKeyMap: {},
          ldClient: undefined,
        }}
      >
        {children}
      </LDFlagsContext.Provider>
    );
  }

  return (
    <LDProvider
      clientSideID={env.LAUNCH_DARKLY_CLIENT_ID}
      context={{
        anonymous: true,
        key: "unauthenticated-employer",
        custom: {
          environment: env.ENVIRONMENT,
        },
      }}
    >
      {children}
    </LDProvider>
  );
};

export const LaunchDarklyIdentifier: React.FC = ({ children }) => {
  if (env.HEADLESS_TESTING) {
    return <>{children}</>;
  }

  const [user] = useUser<User>();
  const [organisation] = useOrganisation();
  const ldClient = useLDClient();
  if (user && organisation) {
    const organisationId = organisation.id;
    const userId = user.id;

    ldClient?.waitUntilReady().then(() => {
      ldClient.identify({
        kind: "multi",
        user: { key: userId },
        organisation: {
          key: organisationId,
          name: organisation.name,
        },
      });
    });
  }

  return <>{children}</>;
};
